import React, {ComponentProps, FC, PropsWithChildren, ReactNode, useState} from 'react';
import styled from 'styled-components';

import {HandlerOf, PredicateOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {TextBox} from '../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {OkCancelDialog} from './okCancelDialog';

/*
 * Props.
 */

interface InputDialogProps extends ComponentProps<typeof OkCancelDialog> {
  initialValue?: string;
  onValueSelect?: HandlerOf<string>;
  hasError?: boolean;
  errorMessage?: ReactNode;
  label?: ReactNode;
  checkIfOkIsDisabled?: PredicateOf<string>;
}

/*
 * Style.
 */

const StyledTextBoxWrapper = styled.div<{$hasLabel?: boolean}>`
  margin-top: ${(p) => p.$hasLabel && '10px'};
`;

/*
 * Component.
 */

export const InputDialog: FC<PropsWithChildren<InputDialogProps>> = (props) => {
  const {initialValue, onValueSelect, checkIfOkIsDisabled, isOkDisabled} = props;
  const [value, setValue] = useState(initialValue || '');

  /*
   * Event handlers.
   */

  const onValueChange: HandlerOf<string> = (newValue) => {
    setValue(newValue);
  };

  const onOkClick = () => {
    if (!onValueSelect) {
      return;
    }

    onValueSelect(value);
  };

  const computeIsOkIsDisabled = () => {
    if (checkIfOkIsDisabled) {
      return checkIfOkIsDisabled(value);
    }

    return isOkDisabled;
  };

  /*
   * Render.
   */

  const {errorMessage, hasError, children, label} = props;
  return (
    <OkCancelDialog {...props} onOkClick={onOkClick} isOkDisabled={computeIsOkIsDisabled()}>
      {children}
      <StyledTextBoxWrapper $hasLabel={Boolean(label)}>
        <TextBox
          value={value}
          onChange={onValueChange}
          errorMessage={errorMessage}
          hasError={hasError}
          label={label}
          shouldAutoFocus
        />
      </StyledTextBoxWrapper>
    </OkCancelDialog>
  );
};
